"use client"

import { getUmbracoMediaUrl } from "@/utils"
import styled from "@emotion/styled"
import CloseIcon from "@mui/icons-material/Close"
import {
  Box,
  Dialog,
  DialogContent,
  IconButton,
  Typography,
} from "@mui/material"
import Image from "next/image"
import Link from "next/link"
import { mediaQueries } from "ui"
import { colors, sizes } from "ui/theme/theme"

const StyledDialog = styled(Dialog)`
  .MuiDialog-paper {
    max-width: 900px;
    width: 90%;
    margin: 0;
    border-radius: 8px;
    overflow: hidden;
    background-color: ${colors.beige};
    padding: 0;

    ${mediaQueries.mobile} {
      width: 85%;
    }
  }
`

const StyledDialogContent = styled(DialogContent)`
  padding: 0;
  display: flex;

  min-height: 400px;
  background-color: ${colors.beige};

  ${mediaQueries.mobile} {
    flex-direction: column;
    min-height: unset;
  }
`

const StyledImageContainer = styled(Box)`
  flex: 1;
  position: relative;

  ${mediaQueries.mobile} {
    min-height: 356px;
  }
  ${mediaQueries.desktopM} {
    min-height: 444px;
  }
  ${mediaQueries.desktopXl} {
    min-height: 500px;
  }
`

const StyledContentContainer = styled(Box)`
  flex: 1;
  padding: ${sizes.baseQuadric};
  display: flex;
  flex-direction: column;
  justify-content: center;

  ${mediaQueries.mobile} {
    padding: ${sizes.baseDouble};
  }
`

const StyledCloseButton = styled(Box)`
  position: absolute;
  top: ${sizes.baseSingle};
  right: ${sizes.baseSingle};
  color: ${colors.black};
  z-index: 1;
  cursor: pointer;

  ${mediaQueries.desktopM} {
    background-color: transparent;
  }
`

const StyledName = styled(Typography)`
  font-weight: 700;
  margin-bottom: ${sizes.baseSingle};
`

const StyledRole = styled(Typography)`
  margin-bottom: ${sizes.baseDouble};
  color: ${colors.red};
`

const StyledText = styled(Typography)`
  margin-top: ${sizes.baseDouble};
  white-space: pre-line;
  color: ${colors.black};
`

const StyledSocialContainer = styled(Box)`
  display: flex;
  margin-bottom: ${sizes.baseDouble};
  margin-left: auto;
`

const StyledSocialIconButton = styled(IconButton)<{
  href: string
  target: string
}>``

const StyledHeaderContainer = styled(Box)`
  display: flex;
  align-items: center;
  margin-bottom: ${sizes.baseSingle};
`
const StyledImageWrapper = styled(Box)`
  cursor: pointer;
  position: relative;
  width: 40px;
  height: 40px;
  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
`

export interface Person {
  fullName: string
  text: string
  role: string
  image: {
    src: string
    alt: string
    width: string
    height: string
  }
  linkedinLink: { text: string; href: string }
  mail: { text: string; href: string }
}

interface SelectPeoplePopupProps {
  person: Person
  open: boolean
  onClose: () => void
}

const SelectPeoplePopup = ({
  person,
  open,
  onClose,
}: SelectPeoplePopupProps) => {
  const hasMailLink = person.mail?.href
  const hasLinkedinLink = person.linkedinLink?.href

  return (
    <StyledDialog open={open} onClose={onClose}>
      <StyledCloseButton onClick={onClose} aria-label="close">
        <CloseIcon />
      </StyledCloseButton>

      <StyledDialogContent>
        {person?.image && (
          <StyledImageContainer
            style={{
              backgroundImage: `url(${person.image.src})`,
            }}
          >
            <Image
              src={getUmbracoMediaUrl(person.image.src)}
              alt={person?.role}
              fill
              style={{ objectFit: "cover", objectPosition: "top" }}
            />
          </StyledImageContainer>
        )}

        <StyledContentContainer>
          <StyledHeaderContainer>
            <Box>
              <StyledName variant="h4">{person.fullName}</StyledName>
              <StyledRole variant="h6">{person.role}</StyledRole>
            </Box>

            <StyledSocialContainer>
              {hasMailLink && (
                <StyledSocialIconButton
                  LinkComponent={Link}
                  aria-label="facebook"
                  href={person.mail.href}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <StyledImageWrapper>
                    <Image alt="" src={"/mail-image.svg"} fill></Image>
                  </StyledImageWrapper>
                </StyledSocialIconButton>
              )}

              {hasLinkedinLink && (
                <StyledSocialIconButton
                  LinkComponent={Link}
                  aria-label="linkedin"
                  href={person.linkedinLink.href}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <StyledImageWrapper>
                    <Image alt="" src={"/linkdin-image.svg"} fill></Image>
                  </StyledImageWrapper>
                </StyledSocialIconButton>
              )}
            </StyledSocialContainer>
          </StyledHeaderContainer>

          <StyledText variant="body1">{person.text}</StyledText>
        </StyledContentContainer>
      </StyledDialogContent>
    </StyledDialog>
  )
}

export default SelectPeoplePopup
